import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';

import {ContextMenuOption} from 'components/ContextMenu';
import {
  getSearchResultPropsForIndex,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {isColumnOutdated} from 'notebook/structured-tables/columns/ColumnDefinitions';
import columnDefinitionToIcon from 'notebook/structured-tables/columns/columnDefinitionToIcon';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {
  StructuredTableColumn,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import TableAxisCell, {DIRECTION} from 'notebook/tables/core-ui/TableAxisCell';
import {getTableCellAffixedProps, allowDeleteColumn} from 'notebook/tables/core-ui/TableCellRenderUtils';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import UnitsTableAxisCell from 'notebook/tables/core-ui/UnitsTableAxisCell';
import {
  Cell,
  ColumnConstraint,
  insertableColumnInfo,
  InsertableColumnInfo,
  SelectionRange,
  Table,
  table,
  TABLE_COLUMN,
  TableColumnType,
  TableDimension,
  TableIndex,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

export interface TableHeaderRowProps
  // Pick off of TableRow props to decrease repetition
  extends Pick<
      TableRowProps,
      'sliceStart' | 'affixedColumnCount' | 'firstColumnWidth' | 'showAffixedShadow' | 'columnWidths'
    >,
    MediocreSearchResultProps {
  // File info
  fileId: string;
  isConfig: boolean;

  // Table info
  table: Table;
  canAddRemoveColumns: boolean;
  ghostCellsCount?: number;

  // Selection state
  isTableSelected: boolean;
  maxJ: number | null;
  minJ: number | null;

  // Rendering slice (see TableRow.js)
  sliceEnd: number | null;
  rowHeight?: number | null;

  // Rendering config
  locked: boolean;
  viewer: boolean;
  disableLegacyHeader: boolean;
  isLeftAlignColumnHeader?: boolean;
  isLeftAlignRowHeader?: boolean;
  showExcessLabels: boolean;
  disableFormulas?: boolean;
  disableColumnLocking?: boolean;
  isProtected?: boolean;
  hideColumnHeaderIcons?: boolean;
  stickyHeaderCells?: boolean; // applies position: sticky to the header cells
  stickyTop?: number; // an offset applied to the sticky header cells

  // Context menu helpers
  getDisallowedColumnNames?: () => Array<string>;
  getExtraContextMenuOptions?: (selectionRange: SelectionRange | null) => Array<ContextMenuOption> | null;
  getSelectionRange: (dim: TableDimension, j: number) => SelectionRange | null;
  insertableColumnInfos?: Array<InsertableColumnInfo>;

  // Table actions
  axisCellDragOverHandler?: (index: TableIndex, e: ElementMouseEvent) => void;
  axisCellDragStartHandler?: (options: {start: TableIndex; active: string}, e: ElementMouseEvent) => void;
  showDragHandle?: boolean;
  axisCellDragHandleMouseDown?: (dim: TableDimension, index: number) => boolean;
  deleteColumns?: (minIndex: number | null, maxIndex: number | null) => void;
  deleteTable?: (() => void) | null;
  finalizeAndExitEditMode?: () => void;
  insertColumnsLeft?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo
  ) => void;
  insertColumnsRight?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo
  ) => void;
  renameColumn?: (i: number, name: string) => void;
  setColumnConstraint?: (
    index: number,
    columnConstraint: ColumnConstraint,
    headerRowCell: Partial<Cell>
  ) => void;
  toggleHeaderColHandler?: () => void;
}

/**
 * The header row in a TableEditable, containing the maximize icon and Excel column labels (A, B, C, ...)
 */
export class TableHeaderRowClass extends React.Component<TableHeaderRowProps> {
  static defaultProps = {
    canAddRemoveColumns: true,
    hideColumnHeaderIcons: false,
    stickyHeaderCells: false,
  };
  static propTypes = {
    fileId: PropTypes.string.isRequired,
    isConfig: PropTypes.bool.isRequired,

    // Table info
    table: table.isRequired,
    canAddRemoveColumns: PropTypes.bool.isRequired,
    ghostCellsCount: PropTypes.number,

    // Selection state
    isTableSelected: PropTypes.bool.isRequired,
    maxJ: PropTypes.number,
    minJ: PropTypes.number,

    // Rendering slice (see TableRow.js)
    sliceStart: PropTypes.number,
    sliceEnd: PropTypes.number,
    rowHeight: PropTypes.number,

    // Rendering config
    locked: PropTypes.bool.isRequired,
    viewer: PropTypes.bool.isRequired,
    disableLegacyHeader: PropTypes.bool.isRequired,
    isLeftAlignColumnHeader: PropTypes.bool,
    isLeftAlignRowHeader: PropTypes.bool,
    showExcessLabels: PropTypes.bool.isRequired,
    disableFormulas: PropTypes.bool,
    isProtected: PropTypes.bool,
    stickyHeaderCells: PropTypes.bool,
    stickyTop: PropTypes.number,
    showDragHandle: PropTypes.bool,

    // Affixed Columns
    affixedColumnCount: PropTypes.number,
    firstColumnWidth: PropTypes.number,
    columnWidths: PropTypes.arrayOf(PropTypes.number),
    showAffixedShadow: PropTypes.bool,

    // Context menu helpers
    getDisallowedColumnNames: PropTypes.func,
    getExtraContextMenuOptions: PropTypes.func,
    getSelectionRange: PropTypes.func.isRequired,
    insertableColumnInfos: PropTypes.arrayOf(insertableColumnInfo.isRequired),

    // Table actions
    axisCellDragOverHandler: PropTypes.func,
    axisCellDragStartHandler: PropTypes.func,
    axisCellDragHandleMouseDown: PropTypes.func,
    deleteColumns: PropTypes.func,
    deleteTable: PropTypes.func,
    finalizeAndExitEditMode: PropTypes.func,
    insertColumnsLeft: PropTypes.func,
    insertColumnsRight: PropTypes.func,
    renameColumn: PropTypes.func,
    setColumnConstraint: PropTypes.func,
    toggleHeaderColHandler: PropTypes.func,
  };

  canModifyCols(): boolean {
    return !this.props.viewer && !this.props.locked && TableUtil.allowColumnEdits(this.props.table);
  }

  /**
   * Determines if a column can be inserted in the specified location.
   *
   * If props.table.isColumnUserConfigurable is present, this method will override
   * props.table.legacyFirstEditableColumnIndex behavior.
   *
   * @param j - index of the column that is selected
   * @param direction - the direction user wishes to insert the column
   * @returns whether a column can be inserted in the specified location.
   */
  canInsertCols(j: number, direction: DIRECTION): boolean {
    // short circuit checks on table config
    if (
      !this.props.canAddRemoveColumns ||
      (TableUtil.disallowCustomColumns(this.props.table) &&
        (this.props.insertableColumnInfos?.length ?? 0) === 0)
    ) {
      return false;
    }

    const range = this.props.getSelectionRange(TABLE_COLUMN, j);
    if (range == null) {
      return false;
    }

    // check for the presence of an isColumnUserConfigurable method
    for (let i = range.minIndex; i <= range.maxIndex; i++) {
      const columnConstraint = this.props.table.columnConstraints?.[i];
      if (columnConstraint && columnConstraint.isColumnUserConfigurable != null) {
        return true;
      }
    }

    // Fall back to deprecated firstEditableColumnIndex behavior
    const firstEditableIndex = TableUtil.legacyFirstEditableColumnIndex(this.props.table);
    if (firstEditableIndex != null) {
      const newIndex = direction === DIRECTION.BEFORE ? range.minIndex : range.maxIndex + 1;
      if (newIndex < firstEditableIndex) {
        return false;
      }
    }

    return true;
  }

  _columnIsOutdated(table: Table, j: number): boolean {
    const columnDefinition = TableUtil.getColumnDefinition(table, j);
    return columnDefinition == null ? false : isColumnOutdated(columnDefinition);
  }

  renderCell(j: number, shouldHighlightCustomColumns: boolean): JSX.Element {
    const {minJ, maxJ, table} = this.props;
    const w = table.table[0].length;
    const isGhostCell = j >= w;
    const editable = this.canModifyCols() && j < w;
    const columnConstraint = table.columnConstraints[j];
    const CellComponent = getCellComponent(table, j);
    const tableCellAffixedProps = getTableCellAffixedProps({...this.props, j});
    const shouldHighlightCell =
      shouldHighlightCustomColumns &&
      columnConstraint?.columnDefinition?.type === TableColumnType.CUSTOM &&
      // Parameter tables use custom cols for datetime values, but these should not be highlighted
      TableUtil.getTableKind(table) !== StructuredTableKind.PARAMETER;
    return (
      <CellComponent
        key={`axis-${j}`}
        disableColumnLocking={this.props.disableColumnLocking}
        disableFormulas={this.props.disableFormulas}
        showExcessLabels={this.props.showExcessLabels}
        renderedLabel={TableUtil.getRenderedColumnLabel(table, j)}
        shouldHighlightCell={shouldHighlightCell}
        label={TableUtil.getColumnLabel(table, j)}
        index={j}
        dim={TABLE_COLUMN}
        {...(editable && {
          delDim: this.props.deleteColumns,
          renameDim: this.props.renameColumn,
          insertDimBefore: this.props.insertColumnsLeft,
          insertDimAfter: this.props.insertColumnsRight,
        })}
        {...(editable &&
          j === 0 &&
          !this.props.disableLegacyHeader && {toggleHeader: this.props.toggleHeaderColHandler})}
        getSelectionRange={this.props.getSelectionRange}
        numDims={w}
        deleteTable={this.props.deleteTable}
        isTableSelected={this.props.isTableSelected}
        {...(!isGhostCell && {
          cellDragStart: this.props.axisCellDragStartHandler,
          cellDragOver: this.props.axisCellDragOverHandler,
          showDragHandle: this.props.showDragHandle,
          dragHandleMouseDown: this.props.axisCellDragHandleMouseDown,
        })}
        isGhostCell={isGhostCell}
        isSelected={minJ != null && maxJ != null && minJ <= j && j <= maxJ}
        {...(CellComponent === UnitsTableAxisCell && {locked: this.props.locked})}
        // Column information
        setColumnConstraint={this.props.setColumnConstraint}
        {...(columnConstraint != null && {columnConstraint})}
        insertableColumnInfos={this.props.insertableColumnInfos}
        getExtraContextMenuOptions={this.props.getExtraContextMenuOptions}
        getDisallowedColumnNames={this.props.getDisallowedColumnNames}
        disallowCustomColumns={TableUtil.disallowCustomColumns(table)}
        shouldDisableInsertDimBefore={!this.canInsertCols(j, DIRECTION.BEFORE)}
        shouldDisableInsertDimAfter={!this.canInsertCols(j, DIRECTION.AFTER)}
        disableInsertDimTooltip={table._disableInsertColsTooltip}
        shouldDisableDeleteDim={!allowDeleteColumn({...this.props, columnIndex: j})}
        iconPrefix={
          this.props.hideColumnHeaderIcons ? null : getIconPrefix(this.props.fileId, table, columnConstraint)
        }
        isLeftAlign={this.props.isLeftAlignColumnHeader}
        columnIsOutdated={this._columnIsOutdated(table, j)}
        isHeaderCell={true}
        dataTestAttrs={{component: 'TableHeaderRow', element: 'columnLabelCell'}}
        isConfig={this.props.isConfig}
        isProtected={this.props.isProtected}
        finalizeAndExitEditMode={this.props.finalizeAndExitEditMode}
        {...tableCellAffixedProps}
        {...getSearchResultPropsForIndex(this.props, j)}
      />
    );
  }

  renderLeadingCell(): JSX.Element | string {
    if (this.props.table._rowHeader == null) {
      return (
        <td
          key="left-col-filter"
          className={classNames(
            'mediocre-tableEditable-axisCell mediocre-tableEditable-axisCell--header cell-sticky',
            {
              'align-left': this.props.isLeftAlignRowHeader,
              'cell-rightShadow': this.props.showAffixedShadow && this.props.affixedColumnCount === 0,
            }
          )}
        />
      );
    }
    return this.props.table._rowHeader;
  }

  render(): JSX.Element {
    const rowStyle = this.props.rowHeight != null ? {height: this.props.rowHeight} : {};
    return (
      <EditorPropertiesContext.Consumer>
        {({highlightCustomColumns}) => (
          <tr
            className={classNames(
              {'mediocre-tableEditable-headerRow--sticky': this.props.stickyHeaderCells},
              {'mediocre-tableEditable-headerRow--noAxis': this.props.firstColumnWidth === 0}
            )}
            style={{
              ...rowStyle,
              top: this.props.stickyHeaderCells ? this.props.stickyTop : undefined,
            }}
          >
            {TableUtil.mapColumns({
              sliceStart: this.props.sliceStart,
              sliceEnd: this.props.sliceEnd,

              ghostCellsCount: this.props.ghostCellsCount ?? 0,
              normalCellsCount: this.props.table.table[0].length,

              mapLeadingCell: () => this.renderLeadingCell(),
              mapNormalCell: (j: number) => this.renderCell(j, highlightCustomColumns || false),
              mapGhostCell: (j: number) => this.renderCell(j, highlightCustomColumns || false),
            })}
            {this.props.stickyHeaderCells && (
              // When the header row is sticky, the top cells will lose their borders due to how tables
              // collapse borders. These are stand-ins for those.
              <>
                <td className="mediocre-tableEditable-headerRow--sticky-borderTop" />
                <td className="mediocre-tableEditable-headerRow--sticky-borderBottom" />
              </>
            )}
          </tr>
        )}
      </EditorPropertiesContext.Consumer>
    );
  }
}

/**
 * Returns column mapping icon if this is a destination column, otherwise the column type icon.
 */
export function getIconPrefix(
  fileId: string,
  table: Table,
  columnConstraint: ColumnConstraint<StructuredTableColumn> | null
): BenchlingIconName | null {
  if (hasDestinationColumnMapping(fileId, table, columnConstraint)) {
    return 'column-mapping';
  }
  if (columnConstraint?.columnDefinition) {
    return columnDefinitionToIcon(columnConstraint.columnDefinition);
  }
  return null;
}

export function getCellComponent(table: Table, j: number): typeof TableAxisCell | typeof UnitsTableAxisCell {
  switch (TableUtil.getColumnDefinitionType(table, j)) {
    case TableColumnType.QUANTITY:
    case TableColumnType.CONCENTRATION:
      return UnitsTableAxisCell;
    default:
      return TableAxisCell;
  }
}

/**
 * Returns true if the given column is a destination column for at least one table mapping
 */
function hasDestinationColumnMapping(
  fileId: string,
  table: Table,
  columnConstraint: ColumnConstraint<StructuredTableColumn> | null
): boolean {
  const tableId = table.provenanceUuid;
  const columnId = columnConstraint?.structuredTableColumn?.provenanceUuid;

  if (tableId == null || columnId == null) {
    return false;
  }

  const structuredTableMappingsState = StructuredTableMappingsGlobalState.getState(fileId);
  const mappingsForColumn = StructuredTableMappingsStateManager.getMappingsForDestinationColumn(
    structuredTableMappingsState,
    tableId,
    columnId
  );
  return Boolean(mappingsForColumn.length);
}

export default React.memo(
  TableHeaderRowClass,
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    // TODO(BNCH-113857): Switch back to shallow equal once redux is enabled
    Object.keys(nextProps).every((key) => isEqual(prevProps[key], nextProps[key]))
);
